import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Search parameter that carries the open evidence drawer, formatted `client:check`. Holding
 * it in the URL makes a drawer linkable and closes it with the back button.
 *
 * @since 1.0.0
 */
export const EVIDENCE_PARAM = 'evidence';

/**
 * Which client and check the drawer is showing.
 *
 * @since 1.0.0
 */
export interface DrawerTarget {
    clientSlug: string;
    checkSlug: string;
}

/**
 * Drawer state plus the two commands that change it.
 *
 * @since 1.0.0
 */
export interface DrawerState {
    target: DrawerTarget | null;
    isOpen: boolean;
    openEvidence: (clientSlug: string, checkSlug: string) => void;
    closeEvidence: () => void;
}

/**
 * Parses the evidence parameter.
 *
 * @since 1.0.0
 * @param raw - the raw parameter value, if present
 * @returns the parsed target, or null when the value is missing or malformed
 */
function parseTarget(raw: string | null): DrawerTarget | null {
    if (raw === null) {
        return null;
    }

    const [clientSlug, checkSlug] = raw.split(':');

    if (!clientSlug || !checkSlug) {
        return null;
    }

    return { clientSlug, checkSlug };
}

/**
 * Drives the evidence drawer from the URL rather than from component state.
 *
 * @since 1.0.0
 * @returns the drawer target and the commands to open or close it
 */
export function useDrawer(): DrawerState {
    const [searchParams, setSearchParams] = useSearchParams();
    const raw = searchParams.get(EVIDENCE_PARAM);

    const target = useMemo(() => parseTarget(raw), [raw]);

    const openEvidence = useCallback(
        (clientSlug: string, checkSlug: string): void => {
            setSearchParams(
                (previous) => {
                    const next = new URLSearchParams(previous);
                    next.set(EVIDENCE_PARAM, `${clientSlug}:${checkSlug}`);

                    return next;
                },
                { preventScrollReset: true },
            );
        },
        [setSearchParams],
    );

    const closeEvidence = useCallback((): void => {
        setSearchParams(
            (previous) => {
                const next = new URLSearchParams(previous);
                next.delete(EVIDENCE_PARAM);

                return next;
            },
            { preventScrollReset: true },
        );
    }, [setSearchParams]);

    return { target, isOpen: target !== null, openEvidence, closeEvidence };
}
